import { Space } from 'antd';
import { useMemo } from 'react';
import styled from 'styled-components';
import { useFormItemScreenReaderLabelAddition } from '../../item/label/useFormItemScreenReaderLabelAddition.tsx';
import { flattenSelectOptions } from '../../utils/flattenSelectOptions.ts';
import { type SelectOption } from '../SelectInput.tsx';
import { StaticInput, type StaticInputProps } from '../static/StaticInput.tsx';
import { StaticTag } from '../static/StaticTag.tsx';

const StaticSelectInput = styled(StaticInput)`
  display: inline-flex;
  padding-right: 3px;
  .ant-input-prefix {
    max-width: 100%;
  }
`;

export interface ISelectReadOnlyProps extends Omit<StaticInputProps, 'value'> {
  options: SelectOption[];
  filterProp: string;
  value: any;
}

export function SelectReadOnly(props: ISelectReadOnlyProps) {
  const { value, options, filterProp, ...inputProps } = props;

  // per ogni valore, ricercare la label
  const selectedOptions = useMemo(() => {
    // le label delle opzioni possono essere un elemento ReactNode
    // o un array di elementi ReactNode, quindi dobbiamo renderizzare un elemento diverso
    const values = Array.isArray(value) ? value : [value];

    // appiattiamo le opzioni per supportare anche quelle raggruppate (divider),
    // dove i valori sono annidati dentro `option.options`
    const flatOptions = flattenSelectOptions(options);

    // scartiamo i valori nullish o non presenti nelle opzioni per evitare
    // di renderizzare tag vuoti e key duplicate/undefined
    return values
      .map(value => flatOptions.find(opt => opt.value === value))
      .filter((option): option is SelectOption => option != null)
      .map(option => ({
        label: option.label,
        value: option.value,
      }));
  }, [options, value]);

  const labelAddition = useMemo(() => {
    return (
      <>
        {selectedOptions.map(option => (
          <span key={option.value}>{option.label}</span>
        ))}
      </>
    );
  }, [selectedOptions]);

  useFormItemScreenReaderLabelAddition(labelAddition);

  return (
    <StaticSelectInput {...inputProps} value={''}>
      <div>
        <Space wrap size={[2, 4]}>
          {selectedOptions.map(option => (
            <StaticTag aria-hidden="true" key={option.value}>
              {option.label}
            </StaticTag>
          ))}
        </Space>
      </div>
    </StaticSelectInput>
  );
}
